import type {
    ViewportLeaderLineDomRendererOptions,
    ViewportLeaderSegment,
    ViewportScreenPoint,
} from './types';

/** 默认引线颜色。 */
const DEFAULT_LEADER_STROKE = '#111827';

/** 默认引线宽度（px）。 */
const DEFAULT_LEADER_STROKE_WIDTH = 1.5;

/** 默认端点圆半径（px）。 */
const DEFAULT_ANCHOR_MARKER_RADIUS = 3;

/**
 * 视口引线 DOM 渲染器。
 *
 * 设计说明：
 * 1. 只负责 SVG 引线段与可选实体端点标记的幂等渲染；
 * 2. 与具体业务（圆角预览、测量浮窗）解耦，由上层组装 segment 数据；
 * 3. 逐帧 sync 时复用 DOM 节点，避免热路径频繁创建/销毁。
 */
export class ViewportLeaderLineDomRenderer {
    /** 叠加层 z-index。 */
    private readonly zIndex: number;
    /** 默认线条颜色。 */
    private readonly defaultStroke: string;
    /** 默认线条宽度（px）。 */
    private readonly defaultStrokeWidth: number;
    /** 根节点。 */
    private rootElement: HTMLDivElement | null = null;
    /** SVG 图层。 */
    private svgElement: SVGSVGElement | null = null;
    /** 引线节点索引。 */
    private readonly leaderLineElements = new Map<string, SVGLineElement>();
    /** 实体端点标记索引。 */
    private readonly anchorMarkerElements = new Map<string, SVGCircleElement>();
    /** 当前帧有效引线 ID。 */
    private readonly activeLeaderIds = new Set<string>();
    /** 当前帧有效端点标记 ID。 */
    private readonly activeAnchorMarkerIds = new Set<string>();

    /**
     * @param options 渲染器参数。
     */
    public constructor(options: ViewportLeaderLineDomRendererOptions = {}) {
        this.zIndex = options.zIndex ?? 12;
        this.defaultStroke = options.stroke ?? DEFAULT_LEADER_STROKE;
        this.defaultStrokeWidth = options.strokeWidth ?? DEFAULT_LEADER_STROKE_WIDTH;
    }

    /**
     * 同步引线段集合。
     *
     * @param segments 引线段集合。
     * @param container 挂载容器。
     */
    public sync(
        segments: ViewportLeaderSegment[],
        container: HTMLElement | null,
    ): void {
        if (!container || segments.length === 0) {
            this.clear();
            return;
        }

        const svgElement = this.ensureSvg(this.ensureRoot(container));
        this.activeLeaderIds.clear();
        this.activeAnchorMarkerIds.clear();

        for (const segment of segments) {
            this.activeLeaderIds.add(segment.id);
            this.updateLeaderLine(
                this.ensureLeaderLine(segment.id, svgElement),
                segment.from,
                segment.to,
                segment.stroke ?? this.defaultStroke,
                segment.strokeWidth ?? this.defaultStrokeWidth,
            );

            if (segment.anchorMarker !== 'dot') {
                continue;
            }

            const markerId = `${segment.id}:anchor-marker`;
            this.activeAnchorMarkerIds.add(markerId);
            this.updateAnchorMarker(
                this.ensureAnchorMarker(markerId, svgElement),
                segment.from,
            );
        }

        this.removeInactiveElements(this.leaderLineElements, this.activeLeaderIds);
        this.removeInactiveElements(this.anchorMarkerElements, this.activeAnchorMarkerIds);
    }

    /**
     * 清空渲染内容。
     */
    public clear(): void {
        if (this.rootElement?.parentElement) {
            this.rootElement.parentElement.removeChild(this.rootElement);
        }
        this.rootElement = null;
        this.svgElement = null;
        this.leaderLineElements.clear();
        this.anchorMarkerElements.clear();
        this.activeLeaderIds.clear();
        this.activeAnchorMarkerIds.clear();
    }

    /**
     * 释放渲染器。
     */
    public dispose(): void {
        this.clear();
    }

    /**
     * 确保根节点存在。
     *
     * @param container 挂载容器。
     * @returns 根节点。
     */
    private ensureRoot(container: HTMLElement): HTMLDivElement {
        if (this.rootElement && this.rootElement.parentElement === container) {
            return this.rootElement;
        }

        this.clear();
        const rootElement = document.createElement('div');
        rootElement.style.position = 'absolute';
        rootElement.style.inset = '0';
        rootElement.style.pointerEvents = 'none';
        rootElement.style.zIndex = String(this.zIndex);
        container.appendChild(rootElement);
        this.rootElement = rootElement;
        return rootElement;
    }

    /**
     * 确保 SVG 图层存在。
     *
     * @param rootElement 根节点。
     * @returns SVG 图层。
     */
    private ensureSvg(rootElement: HTMLDivElement): SVGSVGElement {
        if (this.svgElement && this.svgElement.parentElement === rootElement) {
            return this.svgElement;
        }

        const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svgElement.setAttribute('width', '100%');
        svgElement.setAttribute('height', '100%');
        svgElement.style.position = 'absolute';
        svgElement.style.inset = '0';
        svgElement.style.overflow = 'visible';
        rootElement.appendChild(svgElement);
        this.svgElement = svgElement;
        return svgElement;
    }

    /**
     * 创建或复用引线节点。
     *
     * @param id 引线 ID。
     * @param svgElement SVG 图层。
     * @returns SVG Line。
     */
    private ensureLeaderLine(id: string, svgElement: SVGSVGElement): SVGLineElement {
        const existingElement = this.leaderLineElements.get(id);
        if (existingElement && existingElement.parentNode === svgElement) {
            return existingElement;
        }

        const lineElement = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        svgElement.appendChild(lineElement);
        this.leaderLineElements.set(id, lineElement);
        return lineElement;
    }

    /**
     * 创建或复用实体端点标记。
     *
     * @param id 标记 ID。
     * @param svgElement SVG 图层。
     * @returns SVG Circle。
     */
    private ensureAnchorMarker(id: string, svgElement: SVGSVGElement): SVGCircleElement {
        const existingElement = this.anchorMarkerElements.get(id);
        if (existingElement && existingElement.parentNode === svgElement) {
            return existingElement;
        }

        const markerElement = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        markerElement.setAttribute('r', String(DEFAULT_ANCHOR_MARKER_RADIUS));
        markerElement.setAttribute('fill', DEFAULT_LEADER_STROKE);
        markerElement.setAttribute('stroke', '#ffffff');
        markerElement.setAttribute('stroke-width', '1');
        svgElement.appendChild(markerElement);
        this.anchorMarkerElements.set(id, markerElement);
        return markerElement;
    }

    /**
     * 更新引线位置与样式。
     *
     * @param lineElement 引线节点。
     * @param from 实体侧锚点。
     * @param to 标注侧锚点。
     * @param stroke 线条颜色。
     * @param strokeWidth 线条宽度。
     */
    private updateLeaderLine(
        lineElement: SVGLineElement,
        from: ViewportScreenPoint,
        to: ViewportScreenPoint,
        stroke: string,
        strokeWidth: number,
    ): void {
        lineElement.setAttribute('x1', String(from.x));
        lineElement.setAttribute('y1', String(from.y));
        lineElement.setAttribute('x2', String(to.x));
        lineElement.setAttribute('y2', String(to.y));
        lineElement.setAttribute('stroke', stroke);
        lineElement.setAttribute('stroke-width', String(strokeWidth));
    }

    /**
     * 更新实体端点标记位置。
     *
     * @param markerElement 端点标记节点。
     * @param anchor 实体侧锚点。
     */
    private updateAnchorMarker(
        markerElement: SVGCircleElement,
        anchor: ViewportScreenPoint,
    ): void {
        markerElement.setAttribute('cx', String(anchor.x));
        markerElement.setAttribute('cy', String(anchor.y));
    }

    /**
     * 移除当前帧不再可见的 DOM 节点。
     *
     * @param elements DOM 节点索引。
     * @param activeIds 当前帧有效 ID。
     */
    private removeInactiveElements<TElement extends Element>(
        elements: Map<string, TElement>,
        activeIds: ReadonlySet<string>,
    ): void {
        for (const [id, element] of elements) {
            if (activeIds.has(id)) {
                continue;
            }
            element.remove();
            elements.delete(id);
        }
    }
}
